import { useQuery } from '@apollo/client';
import { GET_BUSINESS_FUNCTION } from '../../pages/dataInputs/data-inputs/business-functions/queries';
import {
  GET_BUSINESS_FUNCTIONS_SELECTED_IT_APPS,
  GET_LOCATION_BUSINESS_FUNCTIONS_SELECTED_IT_APPS,
} from '../../pages/dataInputs/queries/itApplications';
import { BusinessFunctionData, SelectedITApplications } from './types';

/**
 * Fetches business function data
 * @param businessFunctionId - ID of the business function
 * @returns Object with business function name and error
 */
export const useBusinessFunctionData = (businessFunctionId?: string) => {
  const { data, error } = useQuery<BusinessFunctionData, { uuid: string }>(
    GET_BUSINESS_FUNCTION,
    {
      variables: {
        uuid: businessFunctionId || '',
      },
      skip: !businessFunctionId,
      fetchPolicy: 'cache-first',
    },
  );

  return {
    businessFunctionName: data?.getBusinessFunction.name ?? '',
    businessFnError: error,
  };
};

/**
 * Fetches IT applications for a business function
 * @param businessFunctionId - ID of the business function
 * @returns Object with IT applications data (extracted from first array item), loading state, and error
 */
export const useBusinessFunctionITApplications = (
  businessFunctionIds?: string[],
) => {
  const { data, loading, error } = useQuery<
    { getBusinessFunctionsSelectedItApplications: SelectedITApplications[] },
    { data: string[] }
  >(GET_BUSINESS_FUNCTIONS_SELECTED_IT_APPS, {
    variables: {
      data: businessFunctionIds || [],
    },
    skip: !businessFunctionIds,
    fetchPolicy: 'cache-and-network',
  });

  return {
    // The API is designed to fetch data for multiple business functions at once
    // Even though we're only requesting data for one business function
    // We still need to access the first item [0] from the returned array
    itApplicationsData:
      data?.getBusinessFunctionsSelectedItApplications?.[0]?.itApplications ??
      [],
    loading,
    error,
  };
};

/**
 * Fetches IT applications for a location business function
 * @param businessFunctionId - ID of the business function
 * @param locationId - ID of the location
 * @returns Object with IT applications data, loading state, and error
 */
export const useLocationBusinessFunctionITApplications = (
  businessFunctionId?: string,
  locationId?: string,
) => {
  const { data, loading, error } = useQuery<{
    getLocationBusinessFunctionsSelectedItApplications: SelectedITApplications[];
  }>(GET_LOCATION_BUSINESS_FUNCTIONS_SELECTED_IT_APPS, {
    variables: {
      data: {
        businessFunctionUuids: businessFunctionId ? [businessFunctionId] : [],
        locationUuid: locationId || '',
      },
    },
    skip: !businessFunctionId || !locationId,
    fetchPolicy: 'cache-and-network',
  });

  return {
    itApplicationsData:
      data?.getLocationBusinessFunctionsSelectedItApplications?.[0]
        ?.itApplications ?? [],
    loading,
    error,
  };
};
